import { i18n } from '@/locales'
import axios from 'axios'
import { get as _get } from 'lodash'
import authorized from './requestAuthorized'
import socket from './socket'

const BASEURL = process.env.VUE_APP_API_URL || 'http://localhost:3000/api/'
const SMARKET = 'markets/v1/' // Market Service
const SNEWS = 'news/v1/' // News Service
const SAGGREGATOR = 'aggregator/v1/'

const BACKOFFICE = process.env.VUE_APP_BACKOFFICE_URL || 'http://localhost:82/'

export default {
  namespaced: true,
  modules: {
    socket,
    authorized,
  },
  state: {},
  mutations: {},
  actions: {
    getMarketInfo(__, { market = 'moex' }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SMARKET}${market}/info`)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    getMarketLastDay(__, { market = 'default', ticker = 'GAZP' }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SMARKET}${market}/last/${ticker}`)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    getMarketMetrics(__, { market = 'default', ticker = 'GAZP' }) {
      return new Promise((resolve, reject) => {
        Promise.all([
          axios.get(`${BASEURL}${SMARKET}${market}/52w/${ticker}`),
          axios.get(`${BASEURL}${SMARKET}${market}/3m/${ticker}`),
        ])
          .then((res) =>
            resolve({
              _52w: res[0].data,
              _3m: res[1].data,
            })
          )
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    getGraphData(
      __,
      { market = 'default', ticker = 'GAZP', period = '15m', from = null }
    ) {
      return axios.get(
        `${BASEURL}${SMARKET}${market}/graph/${ticker}/${period}`,
        {
          params: {
            from,
          },
        }
      )
    },
    getNews(__, { ticker = 'GAZP', type = false }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SNEWS}posts/${ticker}`, {
            params: {
              lang: i18n.locale,
              ...(!!type && {
                section: type,
              }),
            },
          })
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    getNewsStatistics(__, { ticker = 'GAZP', type = false }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SNEWS}sentiments/${ticker}`, {
            params: {
              lang: i18n.locale,
              ...(!!type && {
                section: type,
              }),
            },
          })
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    searchNews(__, { ticker = 'GAZP', type = false, query = '' }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SNEWS}posts/${ticker}/search`, {
            params: {
              q: query,
              ...(!!type && {
                section: type,
              }),
            },
          })
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    getTickerInfo(__, { id = 1 }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BACKOFFICE}tickers/${id}`)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    getTickerMarketInfo(__, { market = 'MOEX', ticker = 'GAZP' }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BACKOFFICE}ticker-market-infos`, {
            params: {
              name_eq: `${market}:${ticker}`,
              lang: i18n.locale,
            },
          })
          .then((res) => {
            if (res.data.length > 0) resolve(res.data[0])
            else
              reject(
                `No data from backoffice for ticker ${ticker} and market ${market}`
              )
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
    getMarkets() {
      return axios.get(`${BACKOFFICE}markets`).then((res) => res.data)
    },
    // getTickers() {
    //   return axios.get(`${BACKOFFICE}tickers`).then((res) => res.data)
    // },
    getScreenerBO(__, p) {
      const market = p && p.market
      return new Promise((resolve, reject) => {
        axios
          .get(`${BACKOFFICE}ticker-market-infos-search`, {
            params: {
              _limit: 1000,
              ...(market && { market: market }),
            },
          })
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    getScreenerAPI(__, { market, pageOffset = 0, pageSize = 30 }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SAGGREGATOR}${market}/screener`, {
            params: {
              limit: pageSize,
              start: pageOffset * pageSize,
            },
          })
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    getAllForTicker(
      { commit },
      { market = 'MOEX', ticker = 'GAZP', includePosts = 'false' }
    ) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${SAGGREGATOR}${market}/${ticker}/all`, {
            params: {
              includePosts: includePosts,
            },
          })
          .then((res) => {
            commit(
              'market/updateLastDayPrice',
              _get(res, 'data.lastClose.last', 0),
              {
                root: true,
              }
            )

            commit(
              'market/updateCurrentPrice',
              _get(res, 'data.currentPrice', 0),
              {
                root: true,
              }
            )

            const mapOHLC = (path = '') => (res) => ({
              o: _get(res, path + '.open', 0),
              h: _get(res, path + '.high', 0),
              l: _get(res, path + '.low', 0),
              c: _get(res, path + '.close', 0),
              v: _get(res, path + '.volume', 0),
            })

            const metrics = {
              metrics52w: mapOHLC('data.metrics52W')(res),
              metrics3m: mapOHLC('data.metrics3m')(res),
            }

            if (metrics.metrics52w.h == 0 && metrics.metrics52w.l == 0)
              metrics.metrics52w = mapOHLC('data.metricsDay')(res)
            if (metrics.metrics3m.h == 0 && metrics.metrics3m.l == 0)
              metrics.metrics52w = mapOHLC('data.metricsDay')(res)

            commit('market/updateMetrics', metrics, {
              root: true,
            })
            commit('market/updateDay', mapOHLC('data.metricsDay')(res), {
              root: true,
            })

            resolve(res.data)
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    loadArticle(__, { type = 'magazines', postId = 1 }) {
      return axios.get(`${BASEURL}${SNEWS}articles/${type}/${postId}`)
    },
    loadStats(__, { ticker = 'YNDX', period = '2w' }) {
      return axios.get(`${BASEURL}${SNEWS}graphs/${ticker}/${period}`)
    },
    getScreenerCategories({ dispatch }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`${BACKOFFICE}screener-categories`)
          .then((res) => {
            if (res.data) resolve(res.data)
            else reject(`No data from backoffice for screener categories`)
          })
          .catch((err) => {
            dispatch(
              'layout/showMessage',
              {
                title: 'Error',
                message: `getScreenerCategories ${err}`,
                type: 'error',
              },
              { root: true }
            )
            reject(err)
          })
      })
    },
    getCalendarEvents(__, { selectedView, startDay, endDay }) {
      const localTimeZone = new Date().getTimezoneOffset() * 60 * 1000
      return new Promise((resolve, reject) => {
        axios
          .get(`${BACKOFFICE}events/calendar`, {
            params: {
              detail: selectedView === 'month' ? 'short' : 'all',
              dS: new Date(startDay - localTimeZone).toISOString(),
              dF: new Date(endDay - localTimeZone).toISOString(),
            },
          })
          .then((data) => resolve(data.data))
          .catch((err) => reject(err))
      })
    },
  },
  getters: {},
}
